import {
  arrayJsonFormat,
  booleanJsonFormat,
  JsonFormat,
  numberJsonFormat,
  stringJsonFormat,
  temporalInstantJsonFormat,
  temporalPlainDateJsonFormat,
} from "@redotech/json/format";
import { Json } from "@redotech/json/json";
import {
  amountToMinorUnits,
  minorUnitsToAmount,
} from "@redotech/money/iso4217/currencies";
import {
  WeightUnit as CrdbWeightUnit,
  AttributeType as DbAttributeType,
} from "@redotech/redo-prisma-client/enums";
import * as zExt from "@redotech/rpc/ext";
import { assertNever } from "@redotech/util/type";
import { z } from "zod/v4";
import {
  AttributeDefinitionId,
  attributeDefinitionIdFormat,
  hsCodeIdFormat,
  ProductCategoryId,
  productCategoryIdFormat,
} from "../ids";
import { WeightUnit as AlternateWeightUnit } from "../outbound-labels/util";
import { WeightUnit as AttributeWeightUnit } from "../weight-conversion";

export enum AttributeType {
  ShortText = "short_text",
  LongText = "long_text",
  RichText = "rich_text",
  ShortTextList = "list_short_text",
  Json = "json",
  Date = "date",
  DateTime = "datetime",
  Integer = "integer",
  Float = "float",
  Boolean = "boolean",
  Enum = "enum",
  ProductCategory = "product_category",
  HsCode = "hs_code",
  Weight = "weight",
  Money = "money",
  Dimension = "dimension",
  Volume = "volume",
  Link = "link",
  Color = "color",
}

export const WeightSchema = z.object({
  value: z.number(),
  unit: z.enum(AttributeWeightUnit),
});
export type Weight = z.infer<typeof WeightSchema>;

const alternateWeightUnitToAttributeWeightUnit: Record<
  AlternateWeightUnit,
  AttributeWeightUnit
> = {
  [AlternateWeightUnit.GRAM]: "g",
  [AlternateWeightUnit.KILOGRAM]: "kg",
  [AlternateWeightUnit.OUNCE]: "oz",
  [AlternateWeightUnit.POUND]: "lb",
  [AlternateWeightUnit.TONNES]: "t",
} satisfies Record<AlternateWeightUnit, AttributeWeightUnit>;
const attributeWeightUnitToAlternateWeightUnit: Record<
  AttributeWeightUnit,
  AlternateWeightUnit
> = {
  g: AlternateWeightUnit.GRAM,
  kg: AlternateWeightUnit.KILOGRAM,
  oz: AlternateWeightUnit.OUNCE,
  lb: AlternateWeightUnit.POUND,
  t: AlternateWeightUnit.TONNES,
} satisfies Record<AttributeWeightUnit, AlternateWeightUnit>;

const attributeWeightUnitToCrdbWeightUnit: Record<
  AttributeWeightUnit,
  CrdbWeightUnit
> = {
  g: CrdbWeightUnit.Gram,
  kg: CrdbWeightUnit.Kilogram,
  oz: CrdbWeightUnit.Ounce,
  lb: CrdbWeightUnit.Pound,
  t: CrdbWeightUnit.Tonne,
} satisfies Record<AttributeWeightUnit, CrdbWeightUnit>;

export function alternateWeightToAttributeWeight(
  weight: AlternateWeightUnit,
): AttributeWeightUnit {
  return alternateWeightUnitToAttributeWeightUnit[weight];
}

export function attributeWeightToAlternateWeight(
  weightUnit: AttributeWeightUnit,
): AlternateWeightUnit {
  return attributeWeightUnitToAlternateWeightUnit[weightUnit];
}

export function attributeWeightToCrdbWeight(
  weightUnit: AttributeWeightUnit,
): CrdbWeightUnit {
  return attributeWeightUnitToCrdbWeightUnit[weightUnit];
}

export const ProductCategorySchema = z.object({
  id: productCategoryIdFormat.schema,
  name: z.string(),
  parentId: productCategoryIdFormat.schema.nullable(),
});
export type ProductCategory = z.infer<typeof ProductCategorySchema>;

export const HsCodeSchema = z.object({
  id: hsCodeIdFormat.schema,
  code: z.string(),
  description: z.string(),
  parentId: hsCodeIdFormat.schema.nullable(),
});
export type HsCode = z.infer<typeof HsCodeSchema>;

export const MoneySchema = z.object({
  amount: z.number(),
  currency: z.string(),
});
type Money = z.infer<typeof MoneySchema>;

export const MeasurementSchema = z.object({
  value: z.number(),
  unit: z.string(),
});
type Measurement = z.infer<typeof MeasurementSchema>;

export const LinkSchema = z.object({ text: z.string(), url: z.string() });
type Link = z.infer<typeof LinkSchema>;

export const AttributeValueSchema = {
  [AttributeType.ShortText]: z.string(),
  [AttributeType.LongText]: z.string(),
  [AttributeType.RichText]: z.string(),
  [AttributeType.ShortTextList]: z.array(z.string()),
  [AttributeType.Json]: z.json(),
  [AttributeType.Date]: zExt.plainDate(),
  [AttributeType.DateTime]: zExt.instant(),
  [AttributeType.Integer]: z.bigint(),
  [AttributeType.Float]: z.number(),
  [AttributeType.Boolean]: z.boolean(),
  [AttributeType.Enum]: z.string(),
  [AttributeType.ProductCategory]: ProductCategorySchema,
  [AttributeType.HsCode]: HsCodeSchema,
  [AttributeType.Weight]: WeightSchema,
  [AttributeType.Money]: MoneySchema,
  [AttributeType.Dimension]: MeasurementSchema,
  [AttributeType.Volume]: MeasurementSchema,
  [AttributeType.Link]: LinkSchema,
  [AttributeType.Color]: z.string(),
};

export const AttributeValuesSchema = z.union(
  Object.values(AttributeValueSchema),
);

export type AttributeValueType = {
  [key in AttributeType]: (typeof AttributeValueSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

// JsonFormat for BigInt as string
const bigintJsonFormat: JsonFormat<bigint> = {
  read(json: Json) {
    if (typeof json !== "string" && typeof json !== "number") {
      throw new Error(
        `Expected string or number for bigint, got ${typeof json}`,
      );
    }
    return BigInt(json);
  },
  write(value: bigint) {
    return value.toString();
  },
};

// JsonFormat for Money as [amount, currency]
const moneyJsonFormat: JsonFormat<Money> = {
  read(json: Json) {
    if (!Array.isArray(json) || json.length !== 2) {
      throw new Error("Expected [amount, currency] array for money");
    }
    return { amount: minorUnitsToAmount(json[0], json[1]), currency: json[1] };
  },
  write(value: Money) {
    return [amountToMinorUnits(value.amount, value.currency), value.currency];
  },
};

// JsonFormat for Weight as [value, unit] with WeightUnit enum validation
const weightJsonFormat: JsonFormat<Weight> = {
  read(json: Json) {
    if (!Array.isArray(json) || json.length !== 2) {
      throw new Error("Expected [value, unit] array for weight");
    }
    const unit = json[1] as string;
    if (
      !Object.values(AttributeWeightUnit).includes(unit as AttributeWeightUnit)
    ) {
      throw new Error(`Invalid weight unit: ${unit}`);
    }
    return { value: json[0] as number, unit: unit as AttributeWeightUnit };
  },
  write(value: Weight) {
    return [value.value, value.unit];
  },
};

// JsonFormat for Dimension/Volume as [value, unit]
const measurementJsonFormat: JsonFormat<Measurement> = {
  read(json: Json) {
    if (!Array.isArray(json) || json.length !== 2) {
      throw new Error("Expected [value, unit] array");
    }
    return { value: json[0] as number, unit: json[1] as string };
  },
  write(value: Measurement) {
    return [value.value, value.unit];
  },
};

// JsonFormat for Link as [text, url]
const linkJsonFormat: JsonFormat<Link> = {
  read(json: Json) {
    if (!Array.isArray(json) || json.length !== 2) {
      throw new Error("Expected [text, url] array for link");
    }
    return { text: json[0] as string, url: json[1] as string };
  },
  write(value: Link) {
    return [value.text, value.url];
  },
};
const productCategoryJsonFormat: JsonFormat<ProductCategory> = {
  read(json: Json) {
    if (typeof json === "string") {
      return {
        id: json as ProductCategoryId,
        name: "unresolved",
        parentId: null,
      };
    }
    if (typeof json === "object" && json && "id" in json) {
      return {
        id: json.id as ProductCategoryId,
        name: "unresolved",
        parentId: null,
      };
    }
    throw new Error(`Invalid json value for product category: ${json}`);
  },
  write(value: ProductCategory) {
    return value.id;
  },
};

const hsCodeJsonFormat: JsonFormat<HsCode> = {
  read(json: Json) {
    if (typeof json === "string") {
      return {
        id: hsCodeIdFormat.write(BigInt(0)),
        code: json,
        description: "unresolved",
        parentId: null,
      };
    } else if (
      typeof json === "object" &&
      json &&
      "code" in json &&
      typeof json.code === "string"
    ) {
      return {
        id: hsCodeIdFormat.write(BigInt(0)),
        code: json.code,
        description: "unresolved",
        parentId: null,
      };
    }
    throw new Error(`Invalid json value for hs code: ${json}`);
  },
  write(value: HsCode) {
    return value.code;
  },
};

// JsonFormat for plain Json (identity)
const jsonJsonFormat: JsonFormat<Json> = {
  read(json: Json) {
    return json;
  },
  write(value: Json) {
    return value;
  },
};

/**
 * Maps each AttributeType to its JsonFormat for database serialization
 * Uses simple representations: arrays for structured types, strings for bigints, etc.
 */
export const AttributeJsonFormat: {
  [key in AttributeType]: JsonFormat<AttributeValueType[key]>;
} = {
  [AttributeType.ShortText]: stringJsonFormat,
  [AttributeType.LongText]: stringJsonFormat,
  [AttributeType.RichText]: stringJsonFormat,
  [AttributeType.ShortTextList]: arrayJsonFormat(stringJsonFormat),
  [AttributeType.Json]: jsonJsonFormat,
  [AttributeType.Date]: temporalPlainDateJsonFormat,
  [AttributeType.DateTime]: temporalInstantJsonFormat,
  [AttributeType.Integer]: bigintJsonFormat,
  [AttributeType.Float]: numberJsonFormat,
  [AttributeType.Boolean]: booleanJsonFormat,
  [AttributeType.Enum]: stringJsonFormat,
  [AttributeType.ProductCategory]: productCategoryJsonFormat,
  [AttributeType.HsCode]: hsCodeJsonFormat,
  [AttributeType.Weight]: weightJsonFormat,
  [AttributeType.Money]: moneyJsonFormat,
  [AttributeType.Dimension]: measurementJsonFormat,
  [AttributeType.Volume]: measurementJsonFormat,
  [AttributeType.Link]: linkJsonFormat,
  [AttributeType.Color]: stringJsonFormat,
};

const BaseAttributeSchema = z.object({
  attributeId: attributeDefinitionIdFormat.schema,
  name: z.string(),
  displayName: z.string(),
  description: z.string().optional(),
  fieldMapping: z.string().optional(),
});

export interface BaseAttribute {
  attributeId: AttributeDefinitionId;
  name: string;
  displayName: string;
  description?: string;
  type: AttributeType;
}

const TextAttributeSchema_ = BaseAttributeSchema.extend({
  type: z.union([
    z.literal(AttributeType.ShortText),
    z.literal(AttributeType.LongText),
    z.literal(AttributeType.RichText),
  ]),
  value: z.string().optional(),
});
export const TextAttributeSchema: z.ZodType<TextAttribute> =
  TextAttributeSchema_;
export interface TextAttribute extends z.infer<typeof TextAttributeSchema_> {}

const TextListAttributeSchema_ = BaseAttributeSchema.extend({
  type: z.literal(AttributeType.ShortTextList),
  value: z.array(z.string()).optional(),
});
export const TextListAttributeSchema: z.ZodType<TextListAttribute> =
  TextListAttributeSchema_;
export interface TextListAttribute
  extends z.infer<typeof TextListAttributeSchema_> {}

const IntegerAttributeSchema_ = BaseAttributeSchema.extend({
  type: z.literal(AttributeType.Integer),
  value: z.bigint().optional(),
});
export const IntegerAttributeSchema: z.ZodType<IntegerAttribute> =
  IntegerAttributeSchema_;
export interface IntegerAttribute
  extends z.infer<typeof IntegerAttributeSchema_> {}

const FloatAttributeSchema_ = BaseAttributeSchema.extend({
  type: z.literal(AttributeType.Float),
  value: z.number().optional(),
});
export const FloatAttributeSchema: z.ZodType<FloatAttribute> =
  FloatAttributeSchema_;
export interface FloatAttribute extends z.infer<typeof FloatAttributeSchema_> {}

const BooleanAttributeSchema_ = BaseAttributeSchema.extend({
  type: z.literal(AttributeType.Boolean),
  value: z.boolean().optional(),
});
export const BooleanAttributeSchema: z.ZodType<BooleanAttribute> =
  BooleanAttributeSchema_;
export interface BooleanAttribute
  extends z.infer<typeof BooleanAttributeSchema_> {}

const EnumAttributeSchema_ = BaseAttributeSchema.extend({
  type: z.literal(AttributeType.Enum),
  value: z.string().optional(),
  enumValues: z.array(z.string()).min(1),
});
export const EnumAttributeSchema: z.ZodType<EnumAttribute> =
  EnumAttributeSchema_;
export interface EnumAttribute extends z.infer<typeof EnumAttributeSchema_> {}

const DateAttributeSchema_ = BaseAttributeSchema.extend({
  type: z.literal(AttributeType.Date),
  value: zExt.plainDate().optional(),
});
export const DateAttributeSchema: z.ZodType<DateAttribute> =
  DateAttributeSchema_;
export interface DateAttribute extends z.infer<typeof DateAttributeSchema_> {}

const DateTimeAttributeSchema_ = BaseAttributeSchema.extend({
  type: z.literal(AttributeType.DateTime),
  value: zExt.instant().optional(),
});

export const DateTimeAttributeSchema: z.ZodType<DateTimeAttribute> =
  DateTimeAttributeSchema_;
export interface DateTimeAttribute
  extends z.infer<typeof DateTimeAttributeSchema_> {}

const WeightAttributeSchema_ = BaseAttributeSchema.extend({
  type: z.literal(AttributeType.Weight),
  value: WeightSchema.optional(),
});
export const WeightAttributeSchema: z.ZodType<WeightAttribute> =
  WeightAttributeSchema_;
export interface WeightAttribute
  extends z.infer<typeof WeightAttributeSchema_> {}

const MoneyAttributeSchema_ = BaseAttributeSchema.extend({
  type: z.literal(AttributeType.Money),
  value: MoneySchema.optional(),
});
export const MoneyAttributeSchema: z.ZodType<MoneyAttribute> =
  MoneyAttributeSchema_;
export interface MoneyAttribute extends z.infer<typeof MoneyAttributeSchema_> {}

const DimensionAttributeSchema_ = BaseAttributeSchema.extend({
  type: z.literal(AttributeType.Dimension),
  value: MeasurementSchema.optional(),
});
export const DimensionAttributeSchema: z.ZodType<DimensionAttribute> =
  DimensionAttributeSchema_;
export interface DimensionAttribute
  extends z.infer<typeof DimensionAttributeSchema_> {}

const VolumeAttributeSchema_ = BaseAttributeSchema.extend({
  type: z.literal(AttributeType.Volume),
  value: MeasurementSchema.optional(),
});
export const VolumeAttributeSchema: z.ZodType<VolumeAttribute> =
  VolumeAttributeSchema_;
export interface VolumeAttribute
  extends z.infer<typeof VolumeAttributeSchema_> {}

const LinkAttributeSchema_ = BaseAttributeSchema.extend({
  type: z.literal(AttributeType.Link),
  value: LinkSchema.optional(),
});
export const LinkAttributeSchema: z.ZodType<LinkAttribute> =
  LinkAttributeSchema_;
export interface LinkAttribute extends z.infer<typeof LinkAttributeSchema_> {}

const ColorAttributeSchema_ = BaseAttributeSchema.extend({
  type: z.literal(AttributeType.Color),
  value: z.string().optional(),
});
export const ColorAttributeSchema: z.ZodType<ColorAttribute> =
  ColorAttributeSchema_;
export interface ColorAttribute extends z.infer<typeof ColorAttributeSchema_> {}

const ProductCategoryAttributeSchema_ = BaseAttributeSchema.extend({
  type: z.literal(AttributeType.ProductCategory),
  value: ProductCategorySchema.optional(),
});
export const ProductCategoryAttributeSchema: z.ZodType<ProductCategoryAttribute> =
  ProductCategoryAttributeSchema_;
export interface ProductCategoryAttribute
  extends z.infer<typeof ProductCategoryAttributeSchema_> {}

const HsCodeAttributeSchema_ = BaseAttributeSchema.extend({
  type: z.literal(AttributeType.HsCode),
  value: HsCodeSchema.optional(),
});
export const HsCodeAttributeSchema: z.ZodType<HsCodeAttribute> =
  HsCodeAttributeSchema_;
export interface HsCodeAttribute
  extends z.infer<typeof HsCodeAttributeSchema_> {}

const JsonAttributeSchema_ = BaseAttributeSchema.extend({
  type: z.literal(AttributeType.Json),
  value: z.json().optional(),
});
export const JsonAttributeSchema: z.ZodType<JsonAttribute> =
  JsonAttributeSchema_;
export interface JsonAttribute extends z.infer<typeof JsonAttributeSchema_> {}

const AttributeSchema_ = z.union([
  TextAttributeSchema,
  TextListAttributeSchema,
  JsonAttributeSchema,
  IntegerAttributeSchema,
  FloatAttributeSchema,
  BooleanAttributeSchema,
  EnumAttributeSchema,
  DateAttributeSchema,
  DateTimeAttributeSchema,
  WeightAttributeSchema,
  MoneyAttributeSchema,
  DimensionAttributeSchema,
  VolumeAttributeSchema,
  LinkAttributeSchema,
  ColorAttributeSchema,
  ProductCategoryAttributeSchema,
  HsCodeAttributeSchema,
]);

export type Attribute =
  | TextAttribute
  | TextListAttribute
  | JsonAttribute
  | IntegerAttribute
  | FloatAttribute
  | BooleanAttribute
  | EnumAttribute
  | DateAttribute
  | DateTimeAttribute
  | WeightAttribute
  | MoneyAttribute
  | DimensionAttribute
  | VolumeAttribute
  | LinkAttribute
  | ColorAttribute
  | ProductCategoryAttribute
  | HsCodeAttribute;

export const AttributeSchema: z.ZodType<Attribute> = AttributeSchema_;

export function getValueOfAttributeType<T extends AttributeType>(
  attributeType: T,
  value: unknown,
): AttributeValueType[T] | null {
  if (value === null || value === undefined) {
    return null;
  }

  return AttributeValueSchema[attributeType].parse(
    value,
  ) as AttributeValueType[T];
}

const streamableAttributeTypes = new Set<AttributeType>([
  AttributeType.ShortText,
  AttributeType.LongText,
  AttributeType.RichText,
]);
export function isStreamableAttributeType<T extends AttributeType>(
  attributeType: T,
): boolean {
  return streamableAttributeTypes.has(attributeType);
}

// Convenience function and type assertion to be sure that the enum and the db type are never out of sync.
export function convertDbAttributeTypeToAttributeType(
  dbAttributeType: DbAttributeType,
): AttributeType {
  switch (dbAttributeType) {
    case DbAttributeType.short_text:
      return AttributeType.ShortText;
    case DbAttributeType.list_short_text:
      return AttributeType.ShortTextList;
    case DbAttributeType.long_text:
      return AttributeType.LongText;
    case DbAttributeType.rich_text:
      return AttributeType.RichText;
    case DbAttributeType.json:
      return AttributeType.Json;
    case DbAttributeType.date:
      return AttributeType.Date;
    case DbAttributeType.datetime:
      return AttributeType.DateTime;
    case DbAttributeType.integer:
      return AttributeType.Integer;
    case DbAttributeType.float:
      return AttributeType.Float;
    case DbAttributeType.boolean:
      return AttributeType.Boolean;
    case DbAttributeType.enum:
      return AttributeType.Enum;
    case DbAttributeType.product_category:
      return AttributeType.ProductCategory;
    case DbAttributeType.hs_code:
      return AttributeType.HsCode;
    case DbAttributeType.weight:
      return AttributeType.Weight;
    case DbAttributeType.money:
      return AttributeType.Money;
    case DbAttributeType.dimension:
      return AttributeType.Dimension;
    case DbAttributeType.volume:
      return AttributeType.Volume;
    case DbAttributeType.link:
      return AttributeType.Link;
    case DbAttributeType.color:
      return AttributeType.Color;
    default:
      assertNever(dbAttributeType);
  }
}

export function createSafeNullableEnumSchema<T extends string>(
  schema: z.ZodType<T>,
): z.ZodType<T | null> {
  return z
    .union([schema, z.string(), z.null()])
    .transform((value) => safelyTransformNullableEnumValue(value, schema));
}
function safelyTransformNullableEnumValue<T extends string>(
  value: string | null,
  schema: z.ZodType<T>,
): T | null {
  if (!value) {
    return null;
  }
  return schema.safeParse(value).success ? (value as T) : null;
}
